import React, { useEffect, useState } from 'react';
import {
  ITableMenuAction
} from 'src/components/enhanced-table';
import { DataService } from '../../services/data-service';
import Loader from 'src/components/Loader';
import { toast } from 'react-toastify';
import AddIcon from '@mui/icons-material/Add';
import { Alert } from '@mui/material';
import SimpleConfirmationDialog from 'src/components/SimpleConfirmationDialog';
import PermissionService, { ACTION, ROLE } from 'src/services/permission-service';
import { useAppSelector } from 'src/redux/hooks';
import { StorageUnitDetail, StorageUnitTableRow } from 'src/types/StorageUnit.types';
import { useNavigate } from 'react-router';
import StorageUnitTable from 'src/components/common/StorageUnitTable';
import DownloadIcon from '@mui/icons-material/Download';
import CreateStorageUnitDrawer from './CreateStorageUnitDrawer';
import UpdateStorageUnitDrawer from './UpdateStorageUnitDrawer';

interface StorageUnitWarehouse {
  Id: string;
  Name: string;
}

const StorageUnitListing: React.FC = () => {
  const [storageUnits, setStorageUnits] = useState<Record<string, StorageUnitDetail>>({});
  const [loading, setLoading] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [updateData, setUpdateData] = useState<StorageUnitDetail | undefined>(undefined);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const navigate = useNavigate();

const fetchStorageUnits = async (page = 1, pageSize = 10) => {
  setLoading(true);
  try {
    // Call the paginated endpoint (adjust query names to your backend)
    const res = await DataService.get(`api/StorageUnit?PageNumber=${page}&PageSize=${pageSize}`);
    const json = await res.json();

    if (!res.ok || json?.success === false) {
      throw new Error(json?.message || 'Failed to fetch storage units');
    }

    // Support both paged and non-paged shapes safely
    const items: any[] = Array.isArray(json?.data)
      ? json.data
      : Array.isArray(json?.data?.items)
      ? json.data.items
      : [];

    const result: Record<string, StorageUnitDetail> = {};
    for (const su of items) {
      result[su.id] = {
        id: su.id,
        code: su.code,
        name: su.name,
        warehouseId: su.parentWarehouseId,
        storageUnits: [],
        childUnits: [],
        parentWarehouseName: su.parentWarehouseName,
        parentStorageUnitId: su.parentStorageUnitId,
        parentStorageUnitName: su.parentStorageUnitName ?? '-',
        level: su.level,
        isLeaf: su.isLeaf,
        storageTypeId: su.storageTypeId ?? '',
        storageType: su.storageType,
        isActive: su.isActive ?? true,
        properties: su.properties ?? {}, // handle null -> {}
        hasProperties: !!su.properties && Object.keys(su.properties).length > 0,
        propertyCount: su.properties ? Object.keys(su.properties).length : 0,
        isAvailableForStorage: true,
        createdAt: su.createdAt || '',
        updatedAt: su.updatedAt || '',
        aggregatedUniqueProducts: su.aggregatedUniqueProducts ?? 0,
        aggregatedTotalQuantity: su.aggregatedTotalQuantity ?? 0,
      };
    }

    setStorageUnits(result);
  } catch (e: any) {
    toast.error(e?.message || 'Failed to fetch storage units');
    setStorageUnits({});
  } finally {
    setLoading(false);
  }
};


  useEffect(() => {
    fetchStorageUnits();
  }, []);


  const handleExport = async () => {
    try {
      const response = await DataService.get('api/StorageUnit/export', {});
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'StorageUnits_export.xlsx';
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } else {
        toast.error('Export failed');
      }
    } catch {
      toast.error('Export failed');
    }
  };

  const actions: ITableMenuAction[] = [
    {
      key: 'addStorageUnit',
      title: 'Add Storage Unit',
      icon: <AddIcon />,
      onClick: () => setOpenAdd(true),
      visible: () => true,
      disabled: () => false,
    },
    {
      key: 'export',
      title: 'Export',
      icon: <DownloadIcon />,
      onClick: handleExport,
      visible: () => true,
      disabled: () => false,
    }
  ];

  const dataObj: Record<string, StorageUnitTableRow> = Object.values(storageUnits).reduce((acc, su) => {
    acc[su.id] = {
      Id: su.id,
      Code: su.code,
      Name: su.name,
      parentWarehouseName: su.parentWarehouseName,
      parentStorageUnitName: su.parentStorageUnitName,
      Level: su.level,
      isLeaf: su.isLeaf,
      UniqueProducts: (su as any).aggregatedUniqueProducts ?? (su as any).uniqueProducts ?? 0,
      TotalQuantity: (su as any).aggregatedTotalQuantity ?? (su as any).totalQuantity ?? 0,
    };
    return acc;
  }, {} as Record<string, StorageUnitTableRow>);

  const handleDrawerClose = () => {
    setOpenAdd(false);
    setOpenEdit(false);
    setUpdateData(undefined);
  };

  const handleDrawerSuccess = () => {
    handleDrawerClose();
    fetchStorageUnits();
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      const res = await DataService.delete(`api/storageUnit/delete/${deleteId}`, {});
      let errorMsg = 'Failed to delete storage unit';
      if (res.ok) {
        const response = await res.json();
        if (response.Success === false && response.Message) {
          setDeleteError(response.Message);
        } else {
          toast.success('Storage unit deleted');
          fetchStorageUnits();
        }
      } else {
        try {
          const response = await res.json();
          if (response && response.Message) {
            setDeleteError(response.Message);
          } else {
            setDeleteError(errorMsg);
          }
        } catch {
          setDeleteError(errorMsg);
        }
      }
    } catch {
      setDeleteError('Failed to delete storage unit');
    }
    setDeleteLoading(false);
    setDeleteId(null);
  };

  return (
    <div >
      <title>Storage Units</title>
      {deleteError && (
        <Alert severity="error" sx={{ mb: 2 }}>{deleteError}</Alert>
      )}
      {loading ? (
        <Loader />
      ) : (
        <StorageUnitTable
          data={dataObj}
          loading={loading}
          actions={actions}
          onEdit={(id) => {
            setUpdateData(storageUnits[id]);
            setOpenEdit(true);
          }}
          onDelete={(id) => setDeleteId(id)}
        />
      )}
      {openAdd && (
        <CreateStorageUnitDrawer
          open={openAdd}
          onClose={handleDrawerClose}
          onSuccess={handleDrawerSuccess}
          storageUnits={Object.values(storageUnits)}
        />
      )}
      {openEdit && (
        <UpdateStorageUnitDrawer
          open={openEdit}
          onClose={handleDrawerClose}
          updateData={updateData}
          onSuccess={handleDrawerSuccess}
          storageUnits={Object.values(storageUnits)} warehouses={[]}        />
      )}
      <SimpleConfirmationDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Delete Storage Unit"
        message="Are you sure you want to delete this storage unit? This action cannot be undone."
      />
    </div>
  );
};

export default StorageUnitListing;